import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
	Timestamp,
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	query,
	setDoc,
	updateDoc,
	where,
} from 'firebase/firestore';
import { chunk } from 'lodash';
import BASE_QUERY from '@/features/baseQuery';
import { firestore, toBrokerGroupRefs } from '@/utils/firebase';
import { getActingUser } from '@/features/user/userApi';

const UNLAYER_EMAIL_TEMPLATES_COLLECTION = 'unlayerEmailTemplates';
export const UNLAYER_EMAIL_DRAFTS_COLLECTION = 'unlayerEmailDrafts';

export const toUserRef = (userId) => doc(firestore, 'user', String(userId));
export const toStringOrNull = (value) =>
	value === undefined || value === null || value === '' ? null : String(value);

export const asMillis = (value) => value?.toMillis?.() ?? 0;

export const stripUndefinedDeep = (value) => {
	if (Array.isArray(value)) {
		return value.map((item) => stripUndefinedDeep(item));
	}

	if (value && typeof value === 'object') {
		return Object.entries(value).reduce((acc, [key, nestedValue]) => {
			if (nestedValue === undefined) return acc;
			acc[key] = stripUndefinedDeep(nestedValue);
			return acc;
		}, {});
	}

	return value;
};

export const normalizeDesignJson = (designJson) => {
	if (typeof designJson === 'string') {
		try {
			return stripUndefinedDeep(JSON.parse(designJson));
		} catch (error) {
			throw new Error('Invalid design payload');
		}
	}

	if (designJson && typeof designJson === 'object') {
		return stripUndefinedDeep(designJson);
	}

	throw new Error('Invalid design payload');
};

export const serializeDesignJsonForStorage = (designJson) => {
	const normalized = normalizeDesignJson(designJson);
	try {
		return JSON.stringify(normalized);
	} catch (error) {
		throw new Error('Invalid design payload');
	}
};

export const parseDesignJsonFromStorage = (storedValue) => {
	if (typeof storedValue === 'string') {
		return normalizeDesignJson(storedValue);
	}
	if (storedValue && typeof storedValue === 'object') {
		return stripUndefinedDeep(storedValue);
	}
	return null;
};

export const toError = (error, fallbackMessage) => ({
	status: error?.code ?? 'CUSTOM_ERROR',
	data: {
		message:
			typeof error?.message === 'string' && error.message.length > 0
				? error.message
				: fallbackMessage,
	},
});

export const hasBrokerGroupAccess = (brokerGroupRefs, brokerGroupIds) => {
	const allowedIds = new Set((brokerGroupIds ?? []).map((id) => String(id)));
	return (brokerGroupRefs ?? []).some((ref) => ref?.id && allowedIds.has(String(ref.id)));
};

export const canAccessScopedDocument = (data, { userId, brokerGroupIds = [] }) => {
	const createdById = data?.createdBy?.id ? String(data.createdBy.id) : null;
	if (createdById && userId && createdById === String(userId)) {
		return true;
	}
	return hasBrokerGroupAccess(data?.brokerGroups, brokerGroupIds);
};

export const normalizeScopePayload = ({ scopeType, clientId, dealId }) => {
	const normalizedScopeType = String(scopeType ?? '').trim();
	const normalizedClientId = toStringOrNull(clientId);
	const normalizedDealId = toStringOrNull(dealId);

	if (normalizedScopeType === 'client') {
		if (!normalizedClientId || normalizedDealId) {
			throw new Error('Client-scoped drafts require clientId and must not include dealId');
		}
		return {
			scopeType: 'client',
			clientId: normalizedClientId,
			dealId: null,
		};
	}

	if (normalizedScopeType === 'deal') {
		if (!normalizedClientId || !normalizedDealId) {
			throw new Error('Deal-scoped drafts require both clientId and dealId');
		}
		return {
			scopeType: 'deal',
			clientId: normalizedClientId,
			dealId: normalizedDealId,
		};
	}

	throw new Error('Invalid draft scopeType');
};

export const serializeTemplateSummary = (templateDoc) => {
	const data = templateDoc?.data?.() ?? {};
	return {
		id: templateDoc.id,
		name: data?.name ?? '',
		subject: data?.subject ?? '',
		ownerDocId: data?.ownerDocId ?? null,
		createdById: data?.createdBy?.id ?? null,
		lastUpdated: asMillis(data?.lastUpdated ?? data?.updatedAt),
		createdWhen: asMillis(data?.createdWhen ?? data?.createdAt),
	};
};

export const serializeTemplateDetail = (templateDoc) => {
	const data = templateDoc?.data?.() ?? {};
	return {
		...serializeTemplateSummary(templateDoc),
		ownerDocId: data?.ownerDocId ?? null,
		createdById: data?.createdBy?.id ?? null,
		brokerGroupIds: (data?.brokerGroups ?? []).map((ref) => ref?.id).filter(Boolean),
		designJson: parseDesignJsonFromStorage(data?.designJson),
		attachments: data?.attachments ?? [],
	};
};

export const serializeDraftSummary = (draftDoc) => {
	const data = draftDoc?.data?.() ?? {};
	return {
		id: draftDoc.id,
		scopeType: data?.scopeType ?? 'client',
		clientId: toStringOrNull(data?.clientId),
		dealId: toStringOrNull(data?.dealId),
		subject: data?.subject ?? '',
		lastUpdated: asMillis(data?.lastUpdated ?? data?.updatedAt),
		createdWhen: asMillis(data?.createdWhen ?? data?.createdAt),
	};
};

export const serializeDraftDetail = (draftDoc) => {
	const data = draftDoc?.data?.() ?? {};
	return {
		...serializeDraftSummary(draftDoc),
		ownerDocId: data?.ownerDocId ?? null,
		createdById: data?.createdBy?.id ?? null,
		brokerGroupIds: (data?.brokerGroups ?? []).map((ref) => ref?.id).filter(Boolean),
		designJson: parseDesignJsonFromStorage(data?.designJson),
		attachments: data?.attachments ?? [],
	};
};

export const unlayerEmailTemplatesApi = createApi({
	reducerPath: 'unlayerEmailTemplatesApi',
	tagTypes: [
		'UNLAYER_EMAIL_TEMPLATES',
		'UNLAYER_EMAIL_TEMPLATE',
		'UNLAYER_EMAIL_DRAFTS',
		'UNLAYER_EMAIL_DRAFT',
	],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		getUnlayerEmailTemplateSummaries: builder.query({
			providesTags: ['UNLAYER_EMAIL_TEMPLATES'],
			queryFn: async ({ brokerGroupIds = [] }) => {
				try {
					if (brokerGroupIds.length === 0) {
						return { data: [] };
					}

					const collectionRef = collection(firestore, UNLAYER_EMAIL_TEMPLATES_COLLECTION);

					const brokerGroupRefs = toBrokerGroupRefs(brokerGroupIds);

					const brokerGroupChunks = chunk(brokerGroupIds, 30);

					const templateMap = new Map();

					for (const brokerGroupChunk of brokerGroupChunks) {
						const snapshot = await getDocs(
							query(
								collectionRef,
								where('brokerGroupIds', 'array-contains-any', brokerGroupChunk),
							),
						);

						snapshot.docs.forEach((templateDoc) => {
							templateMap.set(templateDoc.id, serializeTemplateSummary(templateDoc));
						});
					}

					const summaries = Array.from(templateMap.values()).sort(
						(a, b) => b.lastUpdated - a.lastUpdated,
					);

					return { data: summaries };
				} catch (error) {
					console.error('Error fetching Unlayer template summaries', error);

					return {
						error: toError(error, 'Unable to load templates'),
					};
				}
			},
		}),

		getUnlayerEmailTemplateDetail: builder.query({
			providesTags: (result, error, { id }) => [{ type: 'UNLAYER_EMAIL_TEMPLATE', id }],
			queryFn: async ({ id }) => {
				try {
					if (!id) {
						throw new Error('Template id is required');
					}
					const templateRef = doc(firestore, UNLAYER_EMAIL_TEMPLATES_COLLECTION, id);
					const snapshot = await getDoc(templateRef);
					if (!snapshot.exists()) {
						throw new Error('Template not found');
					}
					return {
						data: serializeTemplateDetail(snapshot),
					};
				} catch (error) {
					console.error('Error fetching Unlayer template detail', error);
					return { error: toError(error, 'Unable to load template') };
				}
			},
		}),
		createUnlayerEmailTemplate: builder.mutation({
			invalidatesTags: ['UNLAYER_EMAIL_TEMPLATES'],
			queryFn: async (
				{ name, subject = '', designJson, brokerGroupIds = [], attachments = [] },
				{ dispatch, getState },
			) => {
				try {
					if (!name) {
						throw new Error('Missing required template fields');
					}

					const performer = await getActingUser(dispatch, getState);
					const userId = performer.userId;

					const now = Timestamp.now();
					const payload = {
						name: String(name).trim(),
						subject: String(subject),
						designJson: serializeDesignJsonForStorage(designJson),
						createdAt: now,
						updatedAt: now,
						createdWhen: now,
						lastUpdated: now,
						createdBy: toUserRef(userId),
						brokerGroups: toBrokerGroupRefs(brokerGroupIds),
						brokerGroupIds,
						attachments,
						ownerDocId: userId,
					};

					const result = await addDoc(
						collection(firestore, UNLAYER_EMAIL_TEMPLATES_COLLECTION),
						payload,
					);

					return {
						data: {
							id: result.id,
							name: payload.name,
							subject: payload.subject,
							designJson: normalizeDesignJson(designJson),
							attachments,
							createdWhen: now.toMillis(),
							lastUpdated: now.toMillis(),
							ownerDocId: userId,
							createdById: String(userId),
							brokerGroupIds,
						},
					};
				} catch (error) {
					console.error('Error creating Unlayer template', error);
					return { error: toError(error, 'Unable to create template') };
				}
			},
		}),
		updateUnlayerEmailTemplate: builder.mutation({
			invalidatesTags: (result, error, { id }) => [
				{ type: 'UNLAYER_EMAIL_TEMPLATE', id },
				'UNLAYER_EMAIL_TEMPLATES',
			],
			queryFn: async (
				{ id, name, subject = '', designJson, brokerGroupIds = [], attachments },
				{ dispatch, getState },
			) => {
				try {
					if (!id || !name) {
						throw new Error('Missing required template fields');
					}

					const performer = await getActingUser(dispatch, getState);
					const userId = performer.userId;

					const lastUpdated = Timestamp.now();
					await updateDoc(doc(firestore, UNLAYER_EMAIL_TEMPLATES_COLLECTION, id), {
						name: String(name).trim(),
						subject: String(subject),
						designJson: serializeDesignJsonForStorage(designJson),
						lastUpdated,
						updatedAt: lastUpdated,
						createdBy: toUserRef(userId),
						brokerGroups: toBrokerGroupRefs(brokerGroupIds),
						brokerGroupIds: brokerGroupIds,
						ownerDocId: userId,
						...(attachments !== undefined ? { attachments } : {}),
					});

					return {
						data: {
							id,
							name: String(name).trim(),
							subject: String(subject),
							designJson: normalizeDesignJson(designJson),
							...(attachments !== undefined ? { attachments } : {}),
							lastUpdated: lastUpdated.toMillis(),
							ownerDocId: userId,
							createdById: String(userId),
							brokerGroupIds: (brokerGroupIds ?? []).map(String),
						},
					};
				} catch (error) {
					console.error('Error updating Unlayer template', error);
					return { error: toError(error, 'Unable to update template') };
				}
			},
		}),
		deleteUnlayerEmailTemplate: builder.mutation({
			invalidatesTags: (result, error, { id }) => [
				{ type: 'UNLAYER_EMAIL_TEMPLATE', id },
				'UNLAYER_EMAIL_TEMPLATES',
			],
			queryFn: async ({ id }) => {
				try {
					if (!id) {
						throw new Error('Template id is required');
					}
					await deleteDoc(doc(firestore, UNLAYER_EMAIL_TEMPLATES_COLLECTION, id));
					return { data: { id } };
				} catch (error) {
					console.error('Error deleting Unlayer template', error);
					return { error: toError(error, 'Unable to delete template') };
				}
			},
		}),
		getUnlayerEmailDraftSummaries: builder.query({
			providesTags: ['UNLAYER_EMAIL_DRAFTS'],
			queryFn: async ({ clientId = null, dealId = null, brokerGroupIds = [] }) => {
				try {
					const normalizedClientId = toStringOrNull(clientId);
					const normalizedDealId = toStringOrNull(dealId);

					if (!normalizedClientId && !normalizedDealId) {
						return { data: [] };
					}

					const collectionRef = collection(firestore, UNLAYER_EMAIL_DRAFTS_COLLECTION);
					const brokerGroupRefs = toBrokerGroupRefs(brokerGroupIds);
					const brokerGroupChunks = chunk(brokerGroupIds, 30);

					const draftMap = new Map();

					for (const brokerGroupChunk of brokerGroupChunks) {
						if (normalizedDealId) {
							const dealSnapshot = await getDocs(
								query(
									collectionRef,
									where('brokerGroupIds', 'array-contains-any', brokerGroupChunk),
									where('scopeType', '==', 'deal'),
									where('dealId', '==', normalizedDealId),
								),
							);

							dealSnapshot.docs.forEach((draftDoc) => {
								draftMap.set(draftDoc.id, serializeDraftSummary(draftDoc));
							});
						}

						if (normalizedClientId) {
							const clientSnapshot = await getDocs(
								query(
									collectionRef,
									where('brokerGroupIds', 'array-contains-any', brokerGroupChunk),
									where('scopeType', '==', 'client'),
									where('clientId', '==', normalizedClientId),
								),
							);

							clientSnapshot.docs.forEach((draftDoc) => {
								draftMap.set(draftDoc.id, serializeDraftSummary(draftDoc));
							});
						}
					}

					const summaries = Array.from(draftMap.values()).sort(
						(a, b) => b.lastUpdated - a.lastUpdated,
					);

					return { data: summaries };
				} catch (error) {
					console.error('Error fetching Unlayer draft summaries', error);

					return {
						error: toError(error, 'Unable to load drafts'),
					};
				}
			},
		}),

		getUnlayerEmailDraftDetail: builder.query({
			providesTags: (result, error, { id }) => [{ type: 'UNLAYER_EMAIL_DRAFT', id }],
			queryFn: async ({ id, userId, brokerGroupIds = [] }) => {
				try {
					if (!id) {
						throw new Error('Draft id is required');
					}
					const draftRef = doc(firestore, UNLAYER_EMAIL_DRAFTS_COLLECTION, id);
					const snapshot = await getDoc(draftRef);
					if (!snapshot.exists()) {
						throw new Error('Draft not found');
					}
					const data = snapshot.data();
					if (!canAccessScopedDocument(data, { userId, brokerGroupIds })) {
						throw new Error('Access denied for this draft');
					}
					return {
						data: serializeDraftDetail(snapshot),
					};
				} catch (error) {
					console.error('Error fetching Unlayer draft detail', error);
					return { error: toError(error, 'Unable to load draft') };
				}
			},
		}),
		createUnlayerEmailDraft: builder.mutation({
			invalidatesTags: ['UNLAYER_EMAIL_DRAFTS'],
			queryFn: async (
				{
					id,
					scopeType,
					clientId,
					dealId = null,
					subject,
					designJson,
					brokerGroupIds = [],
					attachments = [],
				},
				{ dispatch, getState },
			) => {
				try {
					// id is reserved up front (createUnlayerDraftId) so the attachments folder
					// is named after the draft.
					if (!id || !subject) {
						throw new Error('Missing required draft fields');
					}
					const performer = await getActingUser(dispatch, getState);
					const userId = performer.userId;

					const normalizedScope = normalizeScopePayload({ scopeType, clientId, dealId });
					const now = Timestamp.now();
					const payload = {
						scopeType: normalizedScope.scopeType,
						clientId: normalizedScope.clientId,
						dealId: normalizedScope.dealId,
						subject: String(subject),
						designJson: serializeDesignJsonForStorage(designJson),
						createdAt: now,
						updatedAt: now,
						createdWhen: now,
						lastUpdated: now,
						createdBy: toUserRef(userId),
						brokerGroups: toBrokerGroupRefs(brokerGroupIds),
						brokerGroupIds,
						ownerDocId: userId,
						attachments,
					};

					await setDoc(doc(firestore, UNLAYER_EMAIL_DRAFTS_COLLECTION, id), payload);

					return {
						data: {
							id,
							scopeType: payload.scopeType,
							clientId: payload.clientId,
							dealId: payload.dealId,
							subject: payload.subject,
							designJson: normalizeDesignJson(designJson),
							createdWhen: now.toMillis(),
							lastUpdated: now.toMillis(),
							ownerDocId: userId,
							createdById: String(userId),
							brokerGroupIds,
							attachments,
						},
					};
				} catch (error) {
					console.error('Error creating Unlayer draft', error);
					return { error: toError(error, 'Unable to create draft') };
				}
			},
		}),
		updateUnlayerEmailDraft: builder.mutation({
			invalidatesTags: (result, error, { id }) => [
				{ type: 'UNLAYER_EMAIL_DRAFT', id },
				'UNLAYER_EMAIL_DRAFTS',
			],
			queryFn: async (
				{ id, subject, designJson, brokerGroupIds = [], attachments = [] },
				{ dispatch, getState },
			) => {
				try {
					if (!id || !subject) {
						throw new Error('Missing required draft fields');
					}
					const performer = await getActingUser(dispatch, getState);
					const userId = performer.userId;

					const draftRef = doc(firestore, UNLAYER_EMAIL_DRAFTS_COLLECTION, id);
					const snapshot = await getDoc(draftRef);
					if (!snapshot.exists()) {
						throw new Error('Draft not found');
					}
					const existingData = snapshot.data();
					if (!canAccessScopedDocument(existingData, { userId, brokerGroupIds })) {
						throw new Error('Access denied for this draft');
					}

					const lastUpdated = Timestamp.now();
					await updateDoc(draftRef, {
						subject: String(subject),
						designJson: serializeDesignJsonForStorage(designJson),
						lastUpdated,
						updatedAt: lastUpdated,
						createdBy: toUserRef(userId),
						ownerDocId: userId,
						attachments,
					});

					return {
						data: {
							id,
							scopeType: existingData?.scopeType ?? 'client',
							clientId: toStringOrNull(existingData?.clientId),
							dealId: toStringOrNull(existingData?.dealId),
							subject: String(subject),
							designJson: normalizeDesignJson(designJson),
							lastUpdated: lastUpdated.toMillis(),
							ownerDocId: userId,
							createdById: String(userId),
							brokerGroupIds: (existingData?.brokerGroups ?? [])
								.map((ref) => ref?.id)
								.filter(Boolean),
							attachments,
						},
					};
				} catch (error) {
					console.error('Error updating Unlayer draft', error);
					return { error: toError(error, 'Unable to update draft') };
				}
			},
		}),
		deleteUnlayerEmailDraft: builder.mutation({
			invalidatesTags: (result, error, { id }) => [
				{ type: 'UNLAYER_EMAIL_DRAFT', id },
				'UNLAYER_EMAIL_DRAFTS',
			],
			queryFn: async ({ id, userId, brokerGroupIds = [] }) => {
				try {
					if (!id) {
						throw new Error('Draft id is required');
					}
					const draftRef = doc(firestore, UNLAYER_EMAIL_DRAFTS_COLLECTION, id);
					const snapshot = await getDoc(draftRef);
					if (!snapshot.exists()) {
						throw new Error('Draft not found');
					}
					const existingData = snapshot.data();
					if (!canAccessScopedDocument(existingData, { userId, brokerGroupIds })) {
						throw new Error('Access denied for this draft');
					}
					await deleteDoc(draftRef);
					return { data: { id } };
				} catch (error) {
					console.error('Error deleting Unlayer draft', error);
					return { error: toError(error, 'Unable to delete draft') };
				}
			},
		}),
		createUnlayerDraftId: builder.mutation({
			queryFn: async () => {
				try {
					const collectionRef = collection(firestore, UNLAYER_EMAIL_DRAFTS_COLLECTION);
					const newDocRef = doc(collectionRef);
					const draftId = newDocRef.id;
					return { data: draftId };
				} catch (error) {
					console.error('Error generating draft ID', error);
					return { error: toError(error, 'Unable to generate draft ID') };
				}
			},
		}),
	}),
});

export const {
	useLazyGetUnlayerEmailTemplateSummariesQuery,
	useLazyGetUnlayerEmailTemplateDetailQuery,
	useCreateUnlayerEmailTemplateMutation,
	useUpdateUnlayerEmailTemplateMutation,
	useDeleteUnlayerEmailTemplateMutation,
	useLazyGetUnlayerEmailDraftSummariesQuery,
	useLazyGetUnlayerEmailDraftDetailQuery,
	useCreateUnlayerEmailDraftMutation,
	useUpdateUnlayerEmailDraftMutation,
	useDeleteUnlayerEmailDraftMutation,
	useCreateUnlayerDraftIdMutation,
} = unlayerEmailTemplatesApi;
